import React, { useMemo, useState } from 'react';
import { Building2Icon, PlusIcon, SearchIcon, ShieldIcon, UserIcon, XIcon } from 'lucide-react';
import { useBilling } from '../../contexts/BillingContext';
import { AccountType, BillingAccount } from '../../types/billing';
import { AccountFormModal } from './AccountFormModal';
type Props = {
  id: string;
  label: string;
  value: BillingAccount | null;
  onChange: (account: BillingAccount | null) => void;
  types?: AccountType[];
  allowCreate?: boolean;
  required?: boolean;
};
const icons: Record<AccountType, typeof UserIcon> = {
  Patient: UserIcon,
  Company: Building2Icon,
  Insurer: ShieldIcon,
  WalkIn: UserIcon
};

/** Searchable patient / payer picker. Patients come from the existing patient register — never duplicated. */
export function AccountPicker({
  id,
  label,
  value,
  onChange,
  types,
  allowCreate = true,
  required
}: Props) {
  const {
    accounts,
    can
  } = useBilling();
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const results = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return [];
    const digits = q.replace(/\D/g, '');
    return accounts.filter(a => a.status === 'Active' && (!types || types.includes(a.type))).filter(a => a.name.toLowerCase().includes(q) || a.bmcFileNumber?.toLowerCase().includes(q) || digits.length >= 3 && a.phone?.replace(/\D/g, '').includes(digits)).slice(0, 8);
  }, [accounts, query, types]);
  if (value) {
    const Icon = icons[value.type];
    return <div data-id="element-2533" data-mp="1t6kej1tqkrmb">
        <p className="text-sm font-medium text-slate-700" data-id="element-2534" data-mp="281qn9wk7v4jc">{label}</p>
        <div className="mt-1 flex items-center gap-3 rounded-md border border-slate-300 bg-slate-50 px-3 py-2" data-id="element-2535" data-mp="2831rfxhkqeaz">
          <Icon className="h-4 w-4 shrink-0 text-slate-500" aria-hidden="true" data-id="element-2536" data-mp="234qj5r6xgbuv" />
          <div className="min-w-0 flex-1" data-id="element-2537" data-mp="3jfcedn9mzg7i">
            <p className="truncate text-sm font-semibold text-slate-900" data-id="element-2538" data-mp="3ei2ok6ic52j5">{value.name}</p>
            <p className="truncate text-xs text-slate-500" data-id="element-2539" data-mp="3ll0tp29gmbfs">{value.type === 'WalkIn' ? 'Walk-in' : value.type}{value.bmcFileNumber ? ` · ${value.bmcFileNumber}` : ''}{value.phone ? ` · ${value.phone}` : ''}</p>
          </div>
          <button type="button" onClick={() => onChange(null)} className="rounded p-1 text-slate-500 hover:bg-slate-200" aria-label={`Clear ${label.toLowerCase()}`} data-id="element-2540" data-mp="3vina5t6eqb1v"><XIcon className="h-4 w-4" data-id="element-2541" data-mp="4aszuqe5rzi9" /></button>
        </div>
      </div>;
  }
  return <div className="relative" data-id="element-2542" data-mp="32wccore3kq9a">
      <label htmlFor={id} className="text-sm font-medium text-slate-700" data-id="element-2543" data-mp="37tckb6qli12i">{label}{required && <span className="text-red-600" data-id="element-2544" data-mp="37r9i7oc64hhf"> *</span>}</label>
      <div className="relative mt-1" data-id="element-2545" data-mp="3crx9mb4b70c6">
        <SearchIcon className="pointer-events-none absolute left-2.5 top-2.5 h-4 w-4 text-slate-400" aria-hidden="true" data-id="element-2546" data-mp="3cqujw6iq33xu" />
        <input id={id} value={query} onChange={e => {
        setQuery(e.target.value);
        setOpen(true);
      }} onFocus={() => setOpen(true)} onBlur={() => window.setTimeout(() => setOpen(false), 150)} placeholder="Search name, file number or phone" autoComplete="off" role="combobox" aria-expanded={open && results.length > 0} aria-controls={`${id}-list`} className="w-full rounded-md border border-slate-300 py-2 pl-8 pr-3 text-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500" data-id="element-2547" data-mp="2k49ql0ioshrn" />
      </div>
      {open && query.trim() && <ul id={`${id}-list`} role="listbox" className="absolute z-30 mt-1 max-h-72 w-full overflow-y-auto rounded-md border border-slate-200 bg-white py-1 shadow-lg" data-id="element-2548" data-mp="nb8n75b0fxzv">
          {results.length === 0 && <li className="px-3 py-2 text-sm text-slate-500" data-id="element-2549" data-mp="eh2bu54mkift">No matching accounts.</li>}
          {results.map(a => {
        const Icon = icons[a.type];
        return <li key={a.id} role="option" aria-selected={false} data-id="element-2550" data-mp="4i4r7dabl923">
                <button type="button" onMouseDown={e => e.preventDefault()} onClick={() => {
            onChange(a);
            setQuery('');
            setOpen(false);
          }} className="flex w-full items-center gap-2.5 px-3 py-2 text-left text-sm hover:bg-indigo-50" data-id="element-2551" data-mp="3vofoqzg4froo">
                  <Icon className="h-4 w-4 shrink-0 text-slate-400" aria-hidden="true" data-id="element-2552" data-mp="24kcoh6c8wbr2" />
                  <span className="min-w-0 flex-1 truncate font-medium text-slate-900" data-id="element-2553" data-mp="1pv83xihz9vfs">{a.name}</span>
                  <span className="shrink-0 text-xs text-slate-500" data-id="element-2554" data-mp="1utrq9k0hh98o">{a.bmcFileNumber ?? (a.type === 'WalkIn' ? 'Walk-in' : a.type)}</span>
                </button>
              </li>;
      })}
        </ul>}
      {allowCreate && can('createDocuments') && <button type="button" onClick={() => setCreating(true)} className="mt-1 inline-flex items-center gap-1 text-xs font-medium text-indigo-700 hover:underline" data-id="element-2555" data-mp="16xx7e5nqdg41">
          <PlusIcon className="h-3 w-3" aria-hidden="true" data-id="element-2556" data-mp="1by1mbr8zq2s5" /> New company, insurer or walk-in account
        </button>}
      {creating && <AccountFormModal onClose={() => setCreating(false)} allowedTypes={(types ?? ['Company', 'Insurer', 'WalkIn']).filter(t => t !== 'Patient')} onSaved={account => {
      setCreating(false);
      onChange(account);
    }} data-id="element-2557" data-mp="3696oss8cc95w" />}
    </div>;
}